// 時限閲覧 (Bronze 3分 / 権限 Silver 15分。ADR-021) のクライアント状態導出。
// 2026-07-06 権限再編 (Documents/Plan/Trial3minutes/Plan20260706.md):
// 旧「1ランク昇格トライアル」から「時限閲覧」に転換。付与ロールは 時限閲覧(12) のみ
// (13 は実装廃止)。3分利用 → 3分終了後5分ブロック → リセット (8分サイクル)。
// サーバーは id_token を拡張せず、時限閲覧は標準 exp(3分) で自然失効するため、
// entitlements(12) と exp/iat (いずれも標準クレーム) だけで判定できる。
// watch は使わず computed で派生 (規約)。
import {
  TRIAL_VIEW_ENTITLEMENT, TRIAL_ACTIVE_SEC, TRIAL_CYCLE_SEC, TRIAL_BLOCKED_SEC,
  TRIAL_ACTIVE_SEC_SILVER, TRIAL_CYCLE_SEC_SILVER,
} from "~/types/entity/user";
import type { Role } from "~/types/entity/user";
import { useUserState } from "~/composables/state/useUserState";
import { useNowTicker } from "~/composables/useNowTicker";

export const useTrialUplift = () => {
  // useUserModel と循環参照しないよう state を直接参照する
  const state = useUserState();
  const token = () => state.get().value.token;
  const now = useNowTicker();
  const nowSec = computed(() => now.value / 1000);

  const ent = computed(() => (token()?.entitlements ?? "").split(" "));
  const has12 = computed(() => ent.value.includes(TRIAL_VIEW_ENTITLEMENT));
  const exp = computed(() => token()?.expiresAt);
  const iat = computed(() => token()?.iat);
  const baseRank = computed<Role | undefined>(() => state.get().value.userInfo?.roles?.[0]);
  // 利用時間/サイクル長はランクで異なる: Silver 15分+5分(20分) / Bronze 3分+5分(8分) (ADR-021)。
  // サーバーはマーカー付与時の権限で決めるため、ブロック中の権限変更による不一致は考慮しない (ADR-021)。
  const isSilverRank = computed(() => baseRank.value === "Silver");
  const trialActiveSec = computed(() => isSilverRank.value ? TRIAL_ACTIVE_SEC_SILVER : TRIAL_ACTIVE_SEC);
  const trialCycleSec = computed(() => isSilverRank.value ? TRIAL_CYCLE_SEC_SILVER : TRIAL_CYCLE_SEC);

  // ウィンドウ内: 時限閲覧(12)を持ち、まだ exp(3分) 内 → △機能を利用できる
  const trialActive = computed(() => has12.value && exp.value !== undefined && nowSec.value < exp.value);
  // ウィンドウ内の残り秒 (ハートゲージ/カウントダウン表示用)
  const trialRemainingSec = computed(() => (trialActive.value && exp.value !== undefined) ? Math.max(0, Math.floor(exp.value - nowSec.value)) : 0);

  // ブロック中: 時限対象ランク(Bronze/Silver)で 12 を持たない base トークン保持中。
  // Staff(準備会)/Gold/Platinum/FreeRenewal は無制限側のため対象外。
  // ※ Iron(会員区分enum=1) は対象外。サーバーが Iron を生成せず(Bronze フォールバック)、validRoleList にも無いため
  //   baseRank に "Iron" は現れない。ここも Bronze/Silver のみを対象とする。
  const eligibleRank = computed(() => baseRank.value === "Bronze" || baseRank.value === "Silver");
  // トークン寿命 (exp - iat)。ブロック中の base トークンは exp=マーカー期限のため寿命 ≤ サイクル長 (Bronze 8分 / Silver 20分)、
  // 一方 通常トークンは寿命 = BearerTokenExpireIn(既定 3600秒)。
  // これにより「一度も付与されていない/Redis fail-closed で付与されなかった通常トークン」を除外し、
  // 「閲覧時間終了」表示の誤表示を防ぐ (Documents/Plan/Trial3minutes/Plan20260706.md)。
  // 【既知の制約 (レビュー指摘・許容)】DB 権限の有効期限が 20分未満の Silver は通常トークンでも寿命 < trialCycleSec
  // となり trialCooldown を誤判定し得る (表示のみの影響。サーバー権威には影響しない)。
  // 頻度が低いため許容する。強化する場合はサーバーがブロック用トークンへ専用クレームを載せる案 (id_token 拡張) だが
  // ADR-012 D7 (id_token 非拡張) とトレードオフのため見送り (Plan20260706 §10)。
  const tokenLifetimeSec = computed(() =>
    (exp.value !== undefined && iat.value !== undefined) ? exp.value - iat.value : undefined);
  // 3分満了した時限トークン(12あり・exp超過)をまだ保持している間 (次のトークン取得まで) もブロック中として扱う。
  // これが無いと満了直後〜再取得まで timerState="none" となり、リセット待ちUI (タイマーバー/#reset-guide) が
  // 消えるのにロックオーバーレイだけ残る (Plan20260706 §13.1 P2 / §13.2 F2。Q-T1 回答: クライアント導出で確定)。
  // iat + サイクル長 (サーバーマーカー期限相当) を過ぎたら従来どおり "none" に戻す
  // (リセット完了後も 00:00 のまま reset 表示が残り続けるのを防ぐ。次のトークン取得で新ウィンドウの active になる)。
  const expiredTrialToken = computed(() =>
    has12.value && exp.value !== undefined && nowSec.value >= exp.value
    && iat.value !== undefined && nowSec.value < iat.value + trialCycleSec.value);
  const trialCooldown = computed(() =>
    expiredTrialToken.value
    || (eligibleRank.value && !has12.value
        && exp.value !== undefined && nowSec.value < exp.value
        // ウィンドウ開始と同じ秒に発行されたブロック用トークンは寿命がちょうどサイクル長になるため <= で判定する
        // (実測: マーカー作成秒の再発行で exp-iat = 480 ちょうどの base トークンを観測。通常トークンは 3600秒)
        && tokenLifetimeSec.value !== undefined && tokenLifetimeSec.value <= trialCycleSec.value));
  // リセット時刻 = 保持している base トークンの exp (サーバーがマーカー期限に一致させている)。
  //   期限切れ時限トークン保持中はマーカー期限相当の iat + trialCycleSec (Bronze 8分 / Silver 20分) を使う。
  //   フォールバック: iat + TRIAL_BLOCKED_SEC(5分)。
  const cooldownEndSec = computed(() =>
    expiredTrialToken.value && iat.value !== undefined
      ? iat.value + trialCycleSec.value
      : exp.value ?? (iat.value !== undefined ? iat.value + TRIAL_BLOCKED_SEC : undefined));
  // リセットまでの残り秒 (ブロック中のカウントダウン表示用。Plan20260706 Q-P8)
  const cooldownRemainingSec = computed(() =>
    (trialCooldown.value && cooldownEndSec.value !== undefined) ? Math.max(0, Math.floor(cooldownEndSec.value - nowSec.value)) : 0);

  // 現在「時限サイクル用の古いトークン」を保持しているか。
  //   = 12 を持つ or eligibleRank(Bronze/Silver) の base トークン(寿命≤サイクル長)。
  // 通常トークン (Gold 等・寿命 3600秒) は該当しない → 誤って破棄しない。
  const holdsTrialCycleToken = computed(() =>
    has12.value
    || (eligibleRank.value && !has12.value
        && tokenLifetimeSec.value !== undefined && tokenLifetimeSec.value <= trialCycleSec.value));

  // 5分ブロックのカウントダウンが尽きた後 (= リセット時刻到達済み) に、まだ古い時限トークンを
  // 保持しているか。カウントダウン表示 (timerState) を読むコンポーネントが useNowTicker の毎秒更新で
  // 再評価するため、watch を使わずに「カウントダウンが 0 になった=ユーザーが認識したタイミング」と
  // 連動して true になる (クライアントのみ。SSR ではトークンを保持したまま)。
  const staleTrialTokenAfterCooldown = computed(() => {
    if (!import.meta.client) return false;
    const end = cooldownEndSec.value;
    return holdsTrialCycleToken.value && end !== undefined && nowSec.value >= end;
  });

  // 上の computed を読んだついでに古い時限トークンを破棄する副作用。
  //   破棄しないと、5分ブロック用 base トークンは exp=リセット時刻のため getValidAuthToken
  //   (useUserModel.ts) がブロック終了後も「有効」と判定し、期限切れ相当の古いトークンで △/▲ を
  //   評価し続けて制限表示のまま復帰できない (再度 Trial 可能領域へアクセスすると制限表示になるバグ)。
  //   破棄後は次アクセスの checkPermission (isLoginAttempted=false) が Cookie2Token を叩き直し、
  //   新ウィンドウの時限トークン (entitlement 12) を取得して閲覧が復帰する。
  // computed 評価の同期中に reactive state を書き換えると再帰更新の恐れがあるため、破棄は
  // queueMicrotask で評価後に逃がす。厳密な「境界1回だけ」ではなく冪等 (token=undefined 後は
  // staleTrialTokenAfterCooldown が false に落ちて no-op)。token のみ破棄し userInfo は残す
  // (再取得は checkPermission に委ねる)。useUserModel と循環参照しないよう state を直接操作する。
  const disposeStaleTrialTokenIfCooldownEnded = () => {
    if (!staleTrialTokenAfterCooldown.value) return;
    queueMicrotask(() => {
      const s = state.get().value;
      if (s.token !== undefined && staleTrialTokenAfterCooldown.value) {
        state.set({ ...s, token: undefined });
      }
    });
  };

  // △機能を現時点で利用できるか (無制限側ランク or ウィンドウ内)。
  // TOP のグレーアウト判定・ナビゲーションゲートで使う。未ログインは false。
  const unlimitedView = computed(() => {
    const roles = state.get().value.userInfo?.roles ?? [];
    return roles.some(r => r === "Staff" || r === "Gold" || r === "Platinum" || r === "FreeRenewal");
  });
  const canViewTimeboxed = computed(() => unlimitedView.value || trialActive.value);

  // ===== タイマーUI 用の状態導出 (Documents/design/comike_timer_ui.md §3.2) =====
  // warning: ウィンドウ内かつ残り60秒未満。reset: ブロック中。unlimited: 無制限ロール。
  // active: ウィンドウ内で残り60秒以上。none: いずれでもない (未ログイン/判定不能)。
  // 仕様の available_after_reset は「ブロック→3分ウィンドウへ復帰」した瞬間で、UI上は active に含める。
  const TRIAL_WARNING_SEC = 60;
  const isFreeUser = computed(() => {
    const rank = baseRank.value;
    return rank === "Bronze" || rank === "Silver";
  });
  const timerState = computed<"active" | "warning" | "reset" | "unlimited" | "none">(() => {
    // カウントダウン表示の再評価に便乗して、リセット時刻到達後の古い時限トークンを破棄する
    // (watch 不使用の代替。破棄自体は queueMicrotask で評価後に逃がす。上のコメント参照)。
    disposeStaleTrialTokenIfCooldownEnded();
    if (unlimitedView.value) return "unlimited";
    if (trialActive.value) return trialRemainingSec.value < TRIAL_WARNING_SEC ? "warning" : "active";
    if (trialCooldown.value) return "reset";
    return "none";
  });

  return {
    trialActive, trialActiveSec, trialRemainingSec, trialCooldown, cooldownEndSec, cooldownRemainingSec,
    unlimitedView, canViewTimeboxed, isFreeUser, isSilverRank, timerState,
  };
};
